import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimesCircle, faDownload } from '@fortawesome/free-solid-svg-icons';
import { getCauzioniRiskDocuments } from '../../../services/axios-client/axeCauzioni';
import { Uploader } from '../../../common/components/redesignComponents/Uploader/Uploader';
import { BadgeStatus, RemoveDocument, UploadedDocumentWrapper } from '../../purchase/Purchase.styles';
import pdfUploaded from '../../../assets/redesign_icons/file.svg';
import { BACKEND_API_URL } from '../../../config';

function InfoDocsList({ documents, baseUrl }) {
  if (!documents || !documents.length) return null;

  return (
    <div className="cauzioni-downloadable-docs">
      <h3>Documenti informativi</h3>
      {documents.map((doc) => (
        <div className="cauzioni-download-item" key={doc.filename}>
          <span className={`cauzioni-download-badge cauzioni-download-badge-${doc.type === 'set_informativo' ? 'set-informativo' : 'pog'}`}>
            {doc.type === 'set_informativo' ? 'SET INFORMATIVO' : 'POG'}
          </span>
          <span className="cauzioni-download-name">{doc.name}</span>
          <a
            className="cauzioni-download-btn"
            href={`${baseUrl}/surety-bonds/downloadable-document/${encodeURIComponent(doc.filename)}`}
          >
            Scarica
          </a>
        </div>
      ))}
    </div>
  );
}

function DocSlot({
  document: doc, stagedFile, onStage, templateDoc, baseUrl,
}) {
  const [downloaded, setDownloaded] = useState(() => {
    if (!templateDoc) return false;
    try { return localStorage.getItem(`cauzioni_downloaded_${templateDoc.filename}`) === '1'; } catch { return false; }
  });

  const handleDownload = () => {
    setDownloaded(true);
    try { localStorage.setItem(`cauzioni_downloaded_${templateDoc.filename}`, '1'); } catch { /* */ }
  };

  if (stagedFile) {
    return (
      <UploadedDocumentWrapper data-testid="cauzioni-doc-slot">
        <RemoveDocument onClick={() => onStage(null)} data-testid="cauzioni-doc-remove">
          <FontAwesomeIcon icon={faTimesCircle} color="red" />
        </RemoveDocument>
        <BadgeStatus background="#009232" color="#ffffff">In attesa di invio</BadgeStatus>
        <img src={pdfUploaded} alt="" width={50} />
        <p data-testid="cauzioni-doc-staged">{stagedFile.name}</p>
        <h3>{doc.name.toUpperCase()}</h3>
      </UploadedDocumentWrapper>
    );
  }

  if (templateDoc && !downloaded) {
    return (
      <div className="cauzioni-doc-slot cauzioni-doc-slot-download" data-testid="cauzioni-doc-slot">
        <p className="cauzioni-doc-slot-name">{doc.name}</p>
        <p className="cauzioni-doc-slot-hint">Scarica il modulo, compilalo e poi caricalo qui.</p>
        <a
          className="cauzioni-download-btn cauzioni-download-btn-full"
          href={`${baseUrl}/surety-bonds/downloadable-document/${encodeURIComponent(templateDoc.filename)}`}
          onClick={handleDownload}
        >
          <FontAwesomeIcon icon={faDownload} size="sm" />
          {' '}
          Scarica modulo da compilare
        </a>
        <button
          type="button"
          className="cauzioni-doc-slot-skip"
          onClick={() => setDownloaded(true)}
        >
          Ho già il documento
        </button>
      </div>
    );
  }

  return (
    <div className="cauzioni-doc-slot" data-testid="cauzioni-doc-slot">
      <p className="cauzioni-doc-slot-name">{doc.name}</p>
      {templateDoc && (
        <a
          className="cauzioni-doc-row-template"
          href={`${baseUrl}/surety-bonds/downloadable-document/${encodeURIComponent(templateDoc.filename)}`}
        >
          <FontAwesomeIcon icon={faDownload} size="xs" />
          {' '}
          Scarica di nuovo il modulo
        </a>
      )}
      <Uploader onFileUpload={onStage} note={`CARICA ${doc.name.toUpperCase()}`} />
    </div>
  );
}

function DocGroup({
  title, documents, stagedFiles, onStage, questionnaireMap, baseUrl,
}) {
  if (!documents.length) return null;

  return (
    <div className="cauzioni-doc-group">
      <h3>{title}</h3>
      <div className="cauzioni-doc-slot-grid">
        {documents.map((doc) => (
          <DocSlot
            key={doc.document_type_id}
            document={doc}
            stagedFile={stagedFiles[doc.document_type_id]}
            onStage={(file) => onStage(doc.document_type_id, file)}
            templateDoc={questionnaireMap.get(doc.document_type_id)}
            baseUrl={baseUrl}
          />
        ))}
      </div>
    </div>
  );
}

function StepDocumenti({
  riskId, stagedFiles, onStage, onCompletionChange, getRiskDocuments = getCauzioniRiskDocuments,
}) {
  const [documents, setDocuments] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;

    if (!riskId) {
      setError(true);
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    getRiskDocuments(riskId)
      .then((res) => { if (!cancelled) setDocuments(res); })
      .catch(() => { if (!cancelled) setError(true); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [riskId]);

  const requiredDocs = documents?.required || [];
  const onRequestDocs = documents?.on_request || [];
  const downloadableDocs = documents?.downloadable_documents || [];
  const infoDocs = downloadableDocs.filter((d) => d.type !== 'questionario');

  const questionnaireMap = new Map(
    downloadableDocs
      .filter((d) => d.type === 'questionario' && d.document_type_id)
      .map((d) => [d.document_type_id, d]),
  );

  const allDocIds = [
    ...requiredDocs.map((d) => d.document_type_id),
    ...onRequestDocs.map((d) => d.document_type_id),
  ];
  const stagedCount = allDocIds.filter((id) => stagedFiles[id]).length;
  const isComplete = allDocIds.length > 0 && stagedCount === allDocIds.length;

  useEffect(() => {
    if (onCompletionChange) onCompletionChange(isComplete);
  }, [isComplete, onCompletionChange]);

  return (
    <div data-testid="cauzioni-step-documenti">
      <div className="cauzioni-info-card">
        <div className="cauzioni-card-body">
          <h2 className="cauzioni-card-title mb-3">Documenti</h2>

          {loading && <p className="cauzioni-empty">Caricamento documenti...</p>}

          {!loading && error && (
            <p className="cauzioni-error">
              Impossibile caricare l&apos;elenco documenti. Riprova più tardi.
            </p>
          )}

          {!loading && !error && documents && (
            <>
              <InfoDocsList documents={infoDocs} baseUrl={BACKEND_API_URL} />

              <p className="cauzioni-count" data-testid="cauzioni-doc-count">
                {`${stagedCount} di ${allDocIds.length} documenti selezionati`}
              </p>

              <DocGroup
                title="DOCUMENTI OBBLIGATORI"
                documents={requiredDocs}
                stagedFiles={stagedFiles}
                onStage={onStage}
                questionnaireMap={questionnaireMap}
                baseUrl={BACKEND_API_URL}
              />
              <DocGroup
                title="DOCUMENTI SU RICHIESTA DELL'ASSUNTORE"
                documents={onRequestDocs}
                stagedFiles={stagedFiles}
                onStage={onStage}
                questionnaireMap={questionnaireMap}
                baseUrl={BACKEND_API_URL}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default StepDocumenti;
